/**
 * Housing-tax relief for a qualifying new home purchase. Prices are before GST/HST.
 *
 * Ontario Regulation 196/26 sections 6-13:
 * https://www.ontario.ca/laws/regulation/r26196
 * Ontario ONHAP overview (updated 2026-09-17):
 * https://www.ontario.ca/page/ontario-new-home-affordability-payment
 * CRA ENHR notice 346:
 * https://www.canada.ca/en/revenue-agency/services/forms-publications/publications/notice346/ontario-enhanced-new-housing-rebate.html
 *
 * Eligibility (agreement date, owner-occupancy, and qualifying ownership)
 * must be checked separately. This function models the stated purchase-price
 * bands after eligibility is established.
 */
export function calculateCanadaHomeRebates(
  price: number,
  firstTimeBuyer: boolean,
  ontario: boolean
) {
  const homePrice = Number.isFinite(price) ? Math.max(0, Math.min(1_000_000_000_000, price)) : 0;
  const federalTax = homePrice * 0.05;

  // CRA's standard federal new-housing rebate is 36% of the 5% GST/HST
  // component, capped at $6,300, and phases out from $350k to $450k.
  // FTHB status can raise the total federal relief to 100% up to $1m.
  // https://www.canada.ca/en/revenue-agency/services/forms-publications/publications/rc4028/gst-hst-new-housing-rebate.html
  const standardFederal =
    homePrice <= 350000
      ? Math.min(federalTax * 0.36, 6300)
      : homePrice < 450000
        ? 6300 * (450000 - homePrice) / 100000
        : 0;
  const federalRebate = firstTimeBuyer
    ? homePrice <= 1000000
      ? federalTax
      : homePrice < 1500000
        ? 50000 * (1500000 - homePrice) / 500000
        : 0
    : standardFederal;

  const enhancedOntario = !ontario
    ? 0
    : homePrice <= 1000000
      ? Math.min(homePrice * 0.08, 80000)
      : homePrice <= 1500000
        ? 80000
        : homePrice < 1850000
          ? 80000 - 56000 * (homePrice - 1500000) / 350000
          : 24000;

  // ONHAP fills only the federal tax still unrecovered through a federal
  // new-housing or FTHB rebate. Above $1.5m, Regulation 196/26 applies
  // 25/28 to the tapering $56k enhanced provincial portion.
  const grossOntarioTopUp = !ontario
    ? 0
    : homePrice <= 1000000
      ? Math.min(federalTax, 50000)
      : homePrice <= 1500000
        ? 50000
        : homePrice < 1850000
          ? 50000 * (1850000 - homePrice) / 350000
          : 0;
  const ontarioTopUp = Math.max(0, grossOntarioTopUp - federalRebate);
  const cents = (amount: number) => Math.round(amount * 100) / 100;
  const fullTax = cents(homePrice * (ontario ? 0.13 : 0.05));
  const roundedFederal = Math.min(cents(federalRebate), cents(federalTax));
  const roundedTopUp = Math.min(cents(ontarioTopUp), Math.max(0, cents(federalTax) - roundedFederal));
  // Rounding each tax slice separately can otherwise exceed invoice HST by
  // a cent at prices whose 5% and 8% components both have half-cent tails.
  const roundedOntario = Math.min(cents(enhancedOntario), Math.max(0, cents(fullTax - roundedFederal - roundedTopUp)));

  return {
    federalRebate: roundedFederal,
    enhancedOntario: roundedOntario,
    ontarioTopUp: roundedTopUp,
    totalRebate: cents(roundedFederal + roundedOntario + roundedTopUp),
  };
}
